import { c as _c } from "react/compiler-runtime";
import clsx from "clsx";
import { FC, MouseEventHandler, PropsWithChildren, ReactNode } from "react";
import Button from "../Button";
import Icon, { IconType } from "../Icon";
export type InlineNotificationType = "info" | "info-marigold" | "warning" | "error" | "neutral";
export type InlineNotificationSize = "sm" | "lg";
export interface InlineNotificationProps {
  type?: InlineNotificationType;
  onClick?: MouseEventHandler;
  className?: string;
  size?: InlineNotificationSize;
  icon?: IconType | ReactNode;
  closeable?: boolean;
  onClose?: VoidFunction;
  dataTestId?: string;
}
const colors = {
  info: "bg-info-50",
  "info-marigold": "bg-marigold-50",
  warning: "bg-warning",
  error: "bg-error-50",
  neutral: "bg-neutral-12"
};
const iconColors = {
  info: "text-info-700",
  "info-marigold": "text-marigold-750",
  warning: "",
  error: "text-error-625",
  neutral: ""
};
interface InlineNotificationIconProps {
  icon: string | ReactNode;
  type: InlineNotificationType;
  size: InlineNotificationSize;
}
const InlineNotificationIcon: FC<InlineNotificationIconProps> = t0 => {
  const $ = _c(5);
  const {
    icon,
    type,
    size
  } = t0;
  if (!icon && type === "error") {
    const t1 = size === "lg" ? 20 : 16;
    let t2;
    if ($[0] !== t1) {
      t2 = <Icon type="Attention" size={t1} />;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (typeof icon === "string") {
    const t1 = icon as IconType;
    const t2 = size === "lg" ? 20 : 16;
    let t3;
    if ($[2] !== t1 || $[3] !== t2) {
      t3 = <Icon type={t1} size={t2} />;
      $[2] = t1;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  return icon;
};
const InlineNotification: FC<PropsWithChildren<InlineNotificationProps>> = t0 => {
  const $ = _c(38);
  let children;
  let className;
  let closeable;
  let icon;
  let onClose;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      type: t1,
      className,
      size: t2,
      icon,
      closeable,
      onClose,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = closeable;
    $[4] = icon;
    $[5] = onClose;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    children = $[1];
    className = $[2];
    closeable = $[3];
    icon = $[4];
    onClose = $[5];
    props = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const type = t1 === undefined ? "info" : t1;
  const size = t2 === undefined ? "sm" : t2;
  const color = colors[type];
  const iconColor = iconColors[type];
  const t3 = size === "sm";
  const t4 = size === "lg";
  let t5;
  if ($[9] !== className || $[10] !== color || $[11] !== t3 || $[12] !== t4) {
    t5 = clsx("flex items-center gap-3xs", color, className, {
      "rounded-xs px-s py-2xs": t3,
      "rounded-m px-s m:px-m py-3xs": t4
    });
    $[9] = className;
    $[10] = color;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== iconColor) {
    t6 = clsx("flex", iconColor);
    $[14] = iconColor;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== icon || $[17] !== size || $[18] !== type) {
    t7 = <InlineNotificationIcon size={size} type={type} icon={icon} />;
    $[16] = icon;
    $[17] = size;
    $[18] = type;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const t9 = size === "lg";
  const t10 = size === "sm";
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = clsx("flex-1", {
      "text-app-h3": t9,
      "text-app-body-1": t10
    });
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== children || $[27] !== t11) {
    t12 = <div className={t11}>{children}</div>;
    $[26] = children;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== closeable || $[30] !== onClose) {
    t13 = closeable && <div className="pr-2xs"><Button variant="ghost" size="sm" iconStart="CloseSmall" onClick={onClose} /></div>;
    $[29] = closeable;
    $[30] = onClose;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== props || $[33] !== t12 || $[34] !== t13 || $[35] !== t5 || $[36] !== t8) {
    t14 = <div className={t5} {...props}>{t8}{t12}{t13}</div>;
    $[32] = props;
    $[33] = t12;
    $[34] = t13;
    $[35] = t5;
    $[36] = t8;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
};
export default InlineNotification;